package com.swetdot.swetgram.protocol.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The error envelope from `ERRORS.md`. Every non-2xx response uses this shape.
 *
 * Clients localize from [ApiError.code]; [ApiError.message] is an English fallback and must
 * not be shown when a localized string exists for the code.
 */
@Serializable
data class ErrorResponse(
    @SerialName("error") val error: ApiError,
)

@Serializable
data class ApiError(
    @SerialName("code") val code: String,
    @SerialName("message") val message: String,
    @SerialName("request_id") val requestId: String,
    @SerialName("validation_errors") val validationErrors: List<ValidationErrorItem>? = null,
)

@Serializable
data class ValidationErrorItem(
    @SerialName("field") val field: String,
    @SerialName("code") val code: String,
    @SerialName("message") val message: String? = null,
)

/**
 * The catalog from `ERRORS.md`, verbatim. Codes are lowercase snake_case Strings rather than
 * an enum so that a code added in a later minor version does not break an older client.
 */
object ErrorCodes {
    // Authentication and account
    const val UNAUTHORIZED = "unauthorized"
    const val INVALID_CREDENTIALS = "invalid_credentials"
    const val EMAIL_NOT_VERIFIED = "email_not_verified"
    const val SESSION_REVOKED = "session_revoked"
    const val REFRESH_TOKEN_REUSE_DETECTED = "refresh_token_reuse_detected"
    const val ACCOUNT_DELETION_SCHEDULED = "account_deletion_scheduled"
    const val CLIENT_UPGRADE_REQUIRED = "client_upgrade_required"
    const val LEGAL_ACCEPTANCE_REQUIRED = "legal_acceptance_required"
    const val MINIMUM_AGE_NOT_MET = "minimum_age_not_met"

    /**
     * The account is suspended by a moderator. Distinct from [INVALID_CREDENTIALS] deliberately:
     * the credentials were correct, and answering a suspended user "wrong password" would send
     * them round a reset loop that can never succeed. It is only ever emitted AFTER a password
     * has verified, so it tells nobody anything about an account they cannot already open.
     */
    const val ACCOUNT_SUSPENDED = "account_suspended"

    // Validation and conflicts
    const val VALIDATION_FAILED = "validation_failed"
    const val USERNAME_TAKEN = "username_taken"
    const val USERNAME_RESERVED = "username_reserved"
    const val USERNAME_CHANGE_TOO_SOON = "username_change_too_soon"
    const val VERSION_CONFLICT = "version_conflict"
    const val IDEMPOTENCY_CONFLICT = "idempotency_conflict"
    const val DELETE_WINDOW_EXPIRED = "delete_window_expired"
    const val PIN_LIMIT_REACHED = "pin_limit_reached"
    const val PURCHASE_ALREADY_LINKED = "purchase_already_linked"
    const val PURCHASE_NOT_VERIFIED = "purchase_not_verified"
    const val TASK_NOT_FOUND = "task_not_found"
    const val TASK_REPLACEMENT_USED = "task_replacement_used"
    const val TASK_DAY_EXPIRED = "task_day_expired"
    const val TASK_STEPS_DISABLED = "task_steps_disabled"
    const val ITEM_NOT_FOUND = "item_not_found"
    const val STREAK_NOT_FOUND = "streak_not_found"
    const val INSUFFICIENT_XP = "insufficient_xp"
    const val MAX_FREEZES_REACHED = "max_freezes_reached"
    const val PICSO_NOT_FOUND = "picso_not_found"
    const val PICSO_EXPIRED = "picso_expired"
    const val PICSO_DAILY_LIMIT_REACHED = "picso_daily_limit_reached"

    // Messaging and blocking
    const val RECIPIENT_UNAVAILABLE = "recipient_unavailable"
    const val MESSAGE_NOT_SENT = "message_not_sent"
    const val CONVERSATION_NOT_FOUND = "conversation_not_found"
    const val NOT_CONVERSATION_MEMBER = "not_conversation_member"
    const val GROUP_CHAT_READ_ONLY = "group_chat_read_only"
    const val GROUP_POSTING_RESTRICTED = "group_posting_restricted"
    const val PLAN_FULL = "plan_full"
    const val PLAN_CLOSED = "plan_closed"
    const val MESSAGE_EDIT_NOT_ALLOWED = "message_edit_not_allowed"
    const val MESSAGE_DELETE_NOT_ALLOWED = "message_delete_not_allowed"

    // Groups
    const val GROUP_FULL = "group_full"
    const val JOIN_REQUEST_PENDING = "join_request_pending"
    const val GROUP_BANNED = "group_banned"
    const val OWNERSHIP_TRANSFER_REQUIRED = "ownership_transfer_required"
    const val INSUFFICIENT_GROUP_ROLE = "insufficient_group_role"

    // Media and notifications
    const val MEDIA_IN_USE = "media_in_use"
    const val UPLOAD_EXPIRED = "upload_expired"
    const val INVALID_MEDIA_TYPE = "invalid_media_type"
    const val MEDIA_TOO_LARGE = "media_too_large"

    /**
     * `deleteUpload` on an id that is unknown, already deleted, or belongs to somebody else --
     * scoped to the caller, so another user's media is indistinguishable from one that never
     * existed. Follows the existing not-found family rather than answering 204, which would
     * report success for doing nothing.
     */
    const val MEDIA_NOT_FOUND = "media_not_found"
    const val PUSH_TOKEN_NOT_FOUND = "push_token_not_found"

    // Moderation
    const val REPORT_NOT_FOUND = "report_not_found"
    const val ADMIN_ROLE_REQUIRED = "admin_role_required"

    // Rate limits
    const val RATE_LIMIT_EXCEEDED = "rate_limit_exceeded"

    /**
     * 503. A dependency the operation needs is not configured or is not answering -- today only
     * object storage, which `POST /uploads/presign` and `POST /uploads/complete` cannot work
     * without.
     *
     * The frozen contract declares no error response for either operation, so this is not a
     * response a conforming deployment is ever meant to produce; see `ERRORS.md` and DECISIONS
     * §10. It exists because the alternative was worse: the refusal used to leave the service as a
     * bare exception, which Ktor's catch-all turned into `internal_error` with a stack trace in
     * the log on every avatar pick, indistinguishable from a crash to a client and to a monitor.
     */
    const val SERVICE_UNAVAILABLE = "service_unavailable"
}

/** Field-level codes used inside [ValidationErrorItem.code]. */
object ValidationCodes {
    const val USERNAME_TAKEN = "username_taken"
    const val USERNAME_RESERVED = "username_reserved"
    const val EMAIL_TAKEN = "email_taken"
    const val INVALID_FORMAT = "invalid_format"
    const val TOO_SHORT = "too_short"
    const val TOO_LONG = "too_long"
    const val REQUIRED = "required"
}

/** Rate-limit headers the contract attaches to 429 responses. */
object RateLimitHeaders {
    const val RETRY_AFTER = "Retry-After"
    const val LIMIT = "X-RateLimit-Limit"
    const val REMAINING = "X-RateLimit-Remaining"
    const val RESET = "X-RateLimit-Reset"
}
